"use client"
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react"
import dynamic from "next/dynamic"
import { GoArrowLeft, GoArrowRight } from "react-icons/go"
import { Useapi } from "@/helpers/apiContext"
import { useRouter } from "next/navigation"
import Image from "next/image"

// Lazy load with proper sizing to prevent layout shift
const ClientsCount = dynamic(() => import("@/components/CommonComponent/ClientsCount"), {
  ssr: false,
  loading: () => <div className="w-24 h-6 bg-white/20 rounded animate-pulse flex-shrink-0" />,
})

// Custom lightweight slider component with fixed dimensions
const OptimizedSlider = ({ children, settings, sliderRef }: any) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesToShow, setSlidesToShow] = useState(4)
  const [isClient, setIsClient] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const autoplayRef = useRef<NodeJS.Timeout>()

  // Prevent hydration mismatch
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Responsive breakpoints with debouncing to prevent excessive re-renders
  useEffect(() => {
    if (!isClient) return

    let timeoutId: NodeJS.Timeout
    const updateSlidesToShow = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const width = window.innerWidth
        let newSlidesToShow = 4
        if (width < 768) newSlidesToShow = 1
        else if (width < 1024) newSlidesToShow = 2
        else if (width < 1580) newSlidesToShow = 3
        else newSlidesToShow = 4

        setSlidesToShow(newSlidesToShow)
      }, 100)
    }

    updateSlidesToShow()
    window.addEventListener("resize", updateSlidesToShow)
    return () => {
      window.removeEventListener("resize", updateSlidesToShow)
      clearTimeout(timeoutId)
    }
  }, [isClient])

  // Autoplay functionality
  useEffect(() => {
    if (settings.autoplay && children?.length > slidesToShow) {
      autoplayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % Math.max(1, children?.length - slidesToShow + 1))
      }, 3000)
    }
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current)
    }
  }, [children?.length, slidesToShow, settings.autoplay])

  // Expose slider methods via ref
  useEffect(() => {
    if (sliderRef) {
      sliderRef.current = {
        slickNext: () => setCurrentSlide((prev) => (prev + 1) % Math.max(1, children?.length - slidesToShow + 1)),
        slickPrev: () =>
          setCurrentSlide((prev) => (prev === 0 ? Math.max(0, children?.length - slidesToShow) : prev - 1)),
      }
    }
  }, [sliderRef, children?.length, slidesToShow])

  const translateX = useMemo(() => {
    return -(currentSlide * (100 / slidesToShow))
  }, [currentSlide, slidesToShow])

  if (!isClient) {
    // Server-side render with default layout to prevent shift
    return (
      <div className="overflow-hidden" style={{ height: "180px" }}>
        <div className="flex">
          {children?.slice(0, 4).map((child: any, index: number) => (
            <div key={index} className="flex-shrink-0 px-2" style={{ width: "25%" }}>
              {child}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden" ref={containerRef} style={{ height: "180px" }}>
      <div
        className="flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(${translateX}%)` }}
      >
        {children?.map((child: any, index: number) => (
          <div key={index} className="flex-shrink-0 px-2" style={{ width: `${100 / slidesToShow}%` }}>
            {child}
          </div>
        ))}
      </div>
    </div>
  )
}

// Optimized video component with proper loading states
const OptimizedVideo = ({ src, poster }: { src: string; poster: string }) => {
  const [shouldLoad, setShouldLoad] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const videoRef = useRef<any>(null)
  const observerRef = useRef<IntersectionObserver>()

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    )

    if (videoRef.current) {
      observerRef.current.observe(videoRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return (
    <div ref={videoRef} className="absolute inset-0">
      {/* Always show poster to prevent layout shift */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-500"
        style={{
          backgroundImage: `url('${poster}')`,
          opacity: isLoaded ? 0 : 1,
        }}
      />

      {shouldLoad && (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={() => setIsLoaded(true)}
          className=" min-h-[700px] xl:min-h-[90vh] lg:min-h-[120vh] hidden sm:block  w-full object-cover"
          style={{ opacity: isLoaded ? 1 : 0 }}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  )
}

// Memoized service card component with fixed dimensions
const ServiceCard = React.memo(({ service, index }: { service: any; index: number }) => (
  <div
    className="rounded-2xl border max-w-[297px] hover:bg-white h-[180px] hover:text-homeblack group p-5 flex flex-col justify-between mx-4 transform transition-all duration-300 hover:scale-105 will-change-transform"
    style={{
      animationDelay: `${index * 100}ms`,
      animation: "fadeInUp 0.6s ease-out forwards",
      minHeight: "180px", // Prevent layout shift
    }}
  >
    <div className="flex justify-between">
      <div className="text-[20px] font-medium group-hover:font-semibold leading-tight">{service.title}</div>
    </div>
    <p className="mt-3 text-[16px] lg:text-[18px] group-hover:text-homegrey leading-relaxed">{service.description}</p>
  </div>
))

ServiceCard.displayName = "ServiceCard"

const Banner = ({ props }: any) => {
  const router = useRouter()
  const [bannerData, setBannerData] = useState<any>()
  console.log(bannerData);
  
  const { toggleDropdown, basic_details } = Useapi()
  const [data, setData] = useState({ first: "", second: "" })
  const [isClient, setIsClient] = useState(false)
  const sliderRef = useRef<any>(null)
  const Router = useRouter()

  // Prevent hydration mismatch
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Memoize banner data processing
  const processedBannerData = useMemo(() => {
    if (!props) return null
    return props
  }, [props])

  useEffect(() => {
    if (processedBannerData) {
      setBannerData(processedBannerData)
    }
  }, [processedBannerData])

  // Memoize string splitting function
  const splitStringByLastWords = useCallback((text: string, numOfWords: number) => {
    const words = text.split(" ")
    if (numOfWords >= words.length) {
      return { first: "", second: text }
    }
    const splitIndex = words.length - numOfWords
    const firstPart = words.slice(0, splitIndex).join(" ")
    const secondPart = words.slice(splitIndex).join(" ")
    return { first: firstPart, second: secondPart }
  }, [])

  useEffect(() => {
    const result = splitStringByLastWords("Recognized Excellence in Digital Marketing!", 3)
    setData(result)
  }, [splitStringByLastWords])

  // Memoize slider settings
  const sliderSettings = useMemo(
    () => ({
      infinite: true,
      arrows: false,
      speed: 200,
      slidesToShow: 4,
      slidesToScroll: 1,
      autoplay: true,
    }),
    [],
  )

  // Memoize phone number
  const phoneNumber = useMemo(() => {
    return basic_details?.basic_details?.[0]?.phonenumber || ""
  }, [basic_details])

  return (
    <>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div
        className="w-full relative bg-blue banner overflow-hidden xl:min-h-[90vh] lg:min-h-[120vh]"
        onClick={() => toggleDropdown("")}
      >
        {!props && (
          <div className="absolute inset-0">
            <Image
              src="/images/home-video-poster.webp"
              alt="Banner background"
              fill
              className="object-cover"
              priority
              sizes="100vw"
              quality={85}
            />
          </div>
        )}

        {props && isClient && (
          <OptimizedVideo
            src="https://ik.imagekit.io/fpx0m26dc/home-banner.mp4?updatedAt=1753943394471"
            poster="/images/home-video-poster.webp"
          />
        )}

        <div
          className="w-full sm:absolute px-6 lg:px-2 h-full top-0 left-0 flex justify-center bg-[#1090D059] items-center ">
          <div
            className="flex flex-col justify-around text-white py-10 lg:py-3 items-start xl:w-[95%] 2xl:w-[80%] mx-auto">
            <div className="2xl:w-[80%] space-y-5">
              {bannerData?.title && (
                <div
                  className="font-bold !text-white text-center sm:text-left text-[28px] lg:text-[32px] xl:text-[48px] 2xl:text-[62px] leading-tight xl:leading-[60px]"
                  dangerouslySetInnerHTML={{
                    __html: bannerData.title,
                  }}
                />
              )}

              {bannerData?.subtitle && (
                <p className="text-[22px] lg:text-[28px] text-center sm:text-left font-semibold mt-2">
                  {bannerData.subtitle}
                </p>
              )}

              {bannerData?.description && (
                <p className="text-[16px] lg:text-[17px] text-center sm:text-left md:mb-8 mt-2">
                  {bannerData.description}
                </p>
              )}

              <div className="flex mt-3 flex-wrap gap-4 justify-center md:justify-start items-center">
                <button className="flex items-center h-[55px] justify-center px-4 md:px-8 bg-white text-pinkdarker font-medium hover:text-white text-[18px] rounded-md group hover:bg-pinkdarker transition duration-300 will-change-transform">
                  <a href={`tel:${phoneNumber}`} className="flex items-center">
                    <span className="transition-transform duration-300 group-hover:-translate-x-2 text-[20px]">
                      Get Your Free SEM Growth Strategy
                    </span>
                    <GoArrowRight className="text-[20px] ml-2 opacity-0 group-hover:opacity-100 transition duration-300 group-hover:translate-x-2" />
                  </a>
                </button>

                <div className="w-fit" onClick={() => Router.push("/get-a-free-quote")}>
                  <button className="flex items-center hover:bg-white bg-pinkdarker hover:text-pinkdarker font-medium justify-center px-4 xl:px-8 text-[18px] text-white rounded-md h-[55px] group transition duration-300 will-change-transform">
                    <span className="transition-transform duration-300 group-hover:-translate-x-2 text-[20px]">
                      Talk to Our SEM Experts
                    </span>
                    <GoArrowRight className="text-[20px] ml-2 opacity-0 group-hover:opacity-100 transition duration-300 group-hover:translate-x-2" />
                  </button>
                </div>

                <ClientsCount />
              </div>
            </div>

            {bannerData?.our_core_services && (
              <div className="mt-5 w-full max-w-[90vw] px-3 lg:px-1 xl:max-w-[70vw]">
                <div className="flex justify-between items-center">
                  <p className="text-[24px] lg:text-[28px] font-medium">{bannerData.our_core_services}</p>
                  <div className="flex gap-2 text-[24px] flex-shrink-0">
                    <div
                      className="arrow left hover:text-pink cursor-pointer text-[24px] transition-colors duration-200 p-1"
                      onClick={() => sliderRef.current?.slickPrev()}
                    >
                      <GoArrowLeft />
                    </div>
                    <div
                      className="arrow right hover:text-pink cursor-pointer text-[24px] transition-colors duration-200 p-1"
                      onClick={() => sliderRef.current?.slickNext()}
                    >
                      <GoArrowRight />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {bannerData?.card && bannerData.card.length > 0 && (
              <div className="mt-5 w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw] xl:max-w-[80vw] 2xl:max-w-[76vw] px-4 sm:px-3 md:px-2">
                <OptimizedSlider ref={sliderRef} settings={sliderSettings}>
                  {bannerData.card.map((service: any, index: number) => (
                    <ServiceCard key={service?.slug || index} service={service} index={index} />
                  ))}
                </OptimizedSlider>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default React.memo(Banner)
